import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
} from '@mui/material';
import {
  GitHub as GitHubIcon,
  Search as SearchIcon,
  BugReport as BugIcon,
  Code as CodeIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Description as DescIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ImportProjectService, { AnalysisResult, ClientOption, NewClient } from '../../services/ImportProjectService';

const steps = ['Enter Repository', 'Analyze Code', 'Review Analysis', 'Assign Client', 'Import Project'];

const ImportProjectPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [repoUrl, setRepoUrl] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  
  // Client selection
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientOption, setClientOption] = useState<'existing' | 'new'>('existing');
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [newClient, setNewClient] = useState<NewClient>({
    name: '',
    website: '',
    industry: '',
    description: '',
  });

  // Load clients on mount
  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoadingClients(true);
      const clientList = await ImportProjectService.getClients();
      setClients(clientList);
      if (clientList.length > 0) {
        setSelectedClientId(clientList[0].value);
      }
    } catch (err) {
      console.error('Failed to load clients:', err);
    } finally {
      setLoadingClients(false);
    }
  };

  const handleAnalyze = async () => {
    if (!repoUrl) {
      setError('Please enter a repository URL');
      return;
    }

    try {
      setAnalyzing(true);
      setError(null);

      const result = await ImportProjectService.analyzeRepository({
        repository_url: repoUrl,
      });
      
      setAnalysisResult(result);
      setActiveStep(2); // Review analysis
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to analyze repository');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleImport = async () => {
    if (!analysisResult) return;

    try {
      setImporting(true);
      setError(null);

      const project = await ImportProjectService.importProject({
        repository_url: repoUrl,
        analysis: analysisResult,
        generate_specs: true,
        client_id: clientOption === 'existing' ? selectedClientId ?? undefined : undefined,
        new_client: clientOption === 'new' ? newClient : undefined,
      });
      
      setActiveStep(4); // Import complete
      
      // Navigate to project details after a delay
      setTimeout(() => {
        navigate(`/dashboard/admin/projects/${project.id}`);
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import project');
    } finally {
      setImporting(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Enter GitHub Repository URL
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Provide the URL of the GitHub repository you want to import. We'll analyze the code,
              detect bugs, and generate project specifications automatically.
            </Typography>
            
            <TextField
              fullWidth
              label="Repository URL"
              placeholder="https://github.com/username/repository"
              value={repoUrl}
              onChange={(e) => setRepoUrl(e.target.value)}
              error={!!error}
              helperText={error || 'Enter a valid GitHub repository URL (public or private with access)'}
              InputProps={{
                startAdornment: <GitHubIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ mb: 3 }}
            />

            <Button
              variant="contained"
              size="large"
              onClick={() => setActiveStep(1)}
              disabled={!repoUrl}
              startIcon={<SearchIcon />}
              fullWidth
            >
              Next: Analyze Repository
            </Button>
          </Box>
        );

      case 1:
        return (
          <Box textAlign="center">
            <Typography variant="h6" gutterBottom>
              Analyzing Repository
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Our AI-powered analyzer is reviewing your code...
            </Typography>

            <CircularProgress size={80} sx={{ mb: 4 }} />

            <Box sx={{ textAlign: 'left', maxWidth: 500, mx: 'auto' }}>
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Cloning repository..."
                    secondary={analyzing ? "In progress" : "Complete"}
                  />
                  {!analyzing && <CheckIcon color="success" />}
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Detecting project type and technologies..."
                    secondary={analyzing ? "In progress" : "Complete"}
                  />
                  {!analyzing && <CheckIcon color="success" />}
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Analyzing code quality and bugs..."
                    secondary={analyzing ? "In progress" : "Complete"}
                  />
                  {!analyzing && <CheckIcon color="success" />}
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Generating project specifications..."
                    secondary={analyzing ? "In progress" : "Complete"}
                  />
                  {!analyzing && <CheckIcon color="success" />}
                </ListItem>
              </List>
            </Box>

            <Button
              variant="contained"
              onClick={handleAnalyze}
              disabled={analyzing}
              sx={{ mt: 3 }}
            >
              Start Analysis
            </Button>
          </Box>
        );

      case 2:
        if (!analysisResult) return null;
        
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Analysis Complete
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Review the automated analysis results before importing the project.
            </Typography>

            <Grid container spacing={3}>
              {/* Project Info */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <CodeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                      Project Information
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      <strong>Name:</strong> {analysisResult.project_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {analysisResult.description}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Chip label={analysisResult.project_type} color="primary" sx={{ mr: 1 }} />
                      {analysisResult.languages.map(lang => (
                        <Chip key={lang} label={lang} variant="outlined" sx={{ mr: 1 }} />
                      ))}
                    </Box>
                    <Box sx={{ mt: 1 }}>
                      {analysisResult.frameworks.map(fw => (
                        <Chip key={fw} label={fw} size="small" sx={{ mr: 1 }} />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Code Quality */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Code Quality Score
                    </Typography>
                    <Typography variant="h3" color="primary" gutterBottom>
                      {analysisResult.code_quality.score}/10
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {analysisResult.has_tests && `Test Coverage: ${analysisResult.test_coverage}%`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {analysisResult.has_documentation ? '✓ Has Documentation' : '✗ No Documentation'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Dependencies */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Dependencies
                    </Typography>
                    <Typography variant="body2">
                      Total: {analysisResult.dependencies.total}
                    </Typography>
                    {analysisResult.dependencies.outdated > 0 && (
                      <Typography variant="body2" color="warning.main">
                        Outdated: {analysisResult.dependencies.outdated}
                      </Typography>
                    )}
                    {analysisResult.dependencies.vulnerable > 0 && (
                      <Typography variant="body2" color="error.main">
                        Vulnerable: {analysisResult.dependencies.vulnerable}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Bugs Found */}
              {analysisResult.bugs_found.length > 0 && (
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        <BugIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                        Issues Found ({analysisResult.bugs_found.length})
                      </Typography>
                      <List>
                        {analysisResult.bugs_found.map((bug, index) => (
                          <React.Fragment key={index}>
                            {index > 0 && <Divider />}
                            <ListItem alignItems="flex-start">
                              <ListItemText
                                primary={
                                  <Box>
                                    <Chip 
                                      label={bug.severity.toUpperCase()} 
                                      color={getSeverityColor(bug.severity) as any}
                                      size="small"
                                      sx={{ mr: 1 }}
                                    />
                                    {bug.file}
                                    {bug.line && ` (Line ${bug.line})`}
                                  </Box>
                                }
                                secondary={
                                  <>
                                    <Typography variant="body2" color="text.primary">
                                      {bug.description}
                                    </Typography>
                                    {bug.suggestion && (
                                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        <strong>Suggestion:</strong> {bug.suggestion}
                                      </Typography>
                                    )}
                                  </>
                                }
                              />
                            </ListItem>
                          </React.Fragment>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>

            <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setActiveStep(0)}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={() => setActiveStep(3)}
                startIcon={<BusinessIcon />}
                fullWidth
              >
                Next: Assign Client
              </Button>
            </Box>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              <BusinessIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Assign Client
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Choose an existing client or create a new one for this project.
            </Typography>

            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">Client Option</FormLabel>
              <RadioGroup
                value={clientOption}
                onChange={(e) => setClientOption(e.target.value as 'existing' | 'new')}
              >
                <FormControlLabel 
                  value="existing" 
                  control={<Radio />} 
                  label="Assign to Existing Client" 
                />
                <FormControlLabel 
                  value="new" 
                  control={<Radio />} 
                  label="Create New Client" 
                />
              </RadioGroup>
            </FormControl>

            {clientOption === 'existing' ? (
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Select Client</InputLabel>
                <Select
                  value={selectedClientId || ''}
                  onChange={(e) => setSelectedClientId(e.target.value as number)}
                  label="Select Client"
                  disabled={loadingClients}
                >
                  {clients.map(client => (
                    <MenuItem key={client.value} value={client.value}>
                      {client.label} {client.website && `(${client.website})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Box>
                <TextField
                  fullWidth
                  label="Client Name"
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  required
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Website"
                  value={newClient.website}
                  onChange={(e) => setNewClient({ ...newClient, website: e.target.value })}
                  placeholder="https://example.com"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Industry"
                  value={newClient.industry}
                  onChange={(e) => setNewClient({ ...newClient, industry: e.target.value })}
                  placeholder="e.g., Technology, Healthcare, Finance"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Description"
                  value={newClient.description}
                  onChange={(e) => setNewClient({ ...newClient, description: e.target.value })}
                  multiline
                  rows={3}
                  placeholder="Brief description of the client and their business"
                />
              </Box>
            )}

            <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setActiveStep(2)}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleImport}
                disabled={
                  importing || 
                  (clientOption === 'existing' && !selectedClientId) ||
                  (clientOption === 'new' && !newClient.name)
                }
                startIcon={importing ? <CircularProgress size={20} /> : <CheckIcon />}
                fullWidth
              >
                {importing ? 'Importing...' : 'Import Project & Generate Specs'}
              </Button>
            </Box>
          </Box>
        );

      case 4:
        return (
          <Box textAlign="center">
            <CheckIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Project Imported Successfully!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Your project has been analyzed and imported. Specifications and tasks have been generated.
            </Typography>
            <Alert severity="success" sx={{ mb: 3 }}>
              Redirecting to project details page...
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Import Project from GitHub
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Import an existing GitHub repository with automated code analysis, bug detection, and specification generation.
        </Typography>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && activeStep !== 0 && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 4 }}>
        {renderStepContent()}
      </Paper>
    </Container>
  );
};

export default ImportProjectPage;
